import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  StepIndicator, PrimaryButton, SecondaryButton, InputField, SelectField,
  ErrorMessage, LoadingOverlay, SuccessModal, AutoSaveIndicator, 
  PasswordRequirements, FormCard, LeftSection
} from './DesignSystem';
import { 
  Heart, GraduationCap, AlertCircle, Briefcase, User, 
  Palette, Users, Dog, Zap, DollarSign, Globe, 
  Info, Mail, Lock, Phone, UserCircle,
  ChevronDown, ArrowRight,Eye, EyeOff
} from 'lucide-react';


// Define constants directly in the component instead of importing
const FIELD_LIMITS = {
  name: { min: 2, max: 50 },
  email: { max: 100 },
  password: { min: 6, max: 50 },
  mobile: { max: 10 }
};

const REGEX = {
  name: /^[A-Za-z\s]+$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  mobile: /^\d{10}$/
};

const FundraiserForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    purpose: '',
    name: '',
    email: '',
    password: '',
    mobile: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [apiErrorType, setApiErrorType] = useState('error');
  const [showPurposeDropdown, setShowPurposeDropdown] = useState(false);
  const [selectedPurpose, setSelectedPurpose] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  const purposeOptions = useMemo(() => [
    { value: 'medical', label: 'Medical Expenses', icon: Heart, color: 'from-red-400 to-pink-500', description: 'For healthcare needs' },
    { value: 'education', label: 'Education Fees', icon: GraduationCap, color: 'from-blue-400 to-cyan-500', description: 'For tuition and books' },
    { value: 'emergency', label: 'Emergency Relief', icon: AlertCircle, color: 'from-orange-400 to-red-500', description: 'For urgent situations' },
    { value: 'business', label: 'Business Funding', icon: Briefcase, color: 'from-green-400 to-emerald-500', description: 'For startups and growth' },
    { value: 'personal', label: 'Personal Needs', icon: User, color: 'from-purple-400 to-indigo-500', description: 'For individual goals' },
    { value: 'creative', label: 'Creative Projects', icon: Palette, color: 'from-pink-400 to-rose-500', description: 'For arts and media' },
    { value: 'community', label: 'Community Support', icon: Users, color: 'from-teal-400 to-cyan-500', description: 'For local initiatives' },
    { value: 'animals', label: 'Animal Welfare', icon: Dog, color: 'from-yellow-400 to-amber-500', description: 'For pet care and rescue' },
  ], []);

  const features = useMemo(() => [
    { 
      title: "Quick Setup", 
      description: "Start fundraising in just 2 simple steps", 
      gradient: "from-green-500 to-emerald-600",
      icon: Zap
    },
    { 
      title: "Zero Platform Fees", 
      description: "100% of donations go to your cause", 
      gradient: "from-gsg-teal to-gsg-teal-dark",
      icon: DollarSign
    },
    { 
      title: "Global Support", 
      description: "Reach donors from around the world", 
      gradient: "from-purple-500 to-indigo-600",
      icon: Globe
    }
  ], []);

  // Auto-rotate features
  useEffect(() => {
    const featureInterval = setInterval(() => {
      setCurrentFeature(prev => (prev + 1) % features.length);
    }, 4000);

    return () => {
      clearInterval(featureInterval);
    };
  }, [features.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setShowPurposeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Validation functions - moved to useCallback to prevent recreation
  const validateField = useCallback((name, value) => {
    let error = '';
    
    switch (name) {
      case 'purpose':
        if (!value?.trim()) error = 'Please select a purpose';
        break;
      case 'name':
        if (!value?.trim()) error = 'Name is required';
        else if (value.length < FIELD_LIMITS.name.min) error = `Name must be at least ${FIELD_LIMITS.name.min} characters`;
        else if (!REGEX.name.test(value)) error = 'Name should only contain letters and spaces';
        break;
      case 'email':
        if (!value?.trim()) error = 'Email is required';
        else if (!REGEX.email.test(value)) error = 'Please enter a valid email address';
        break;
      case 'password':
        if (!value) error = 'Password is required';
        else if (value.length < FIELD_LIMITS.password.min) error = `Password must be at least ${FIELD_LIMITS.password.min} characters`;
        break;
      case 'mobile':
        if (!value) error = 'Mobile number is required';
        else if (!REGEX.mobile.test(value)) error = 'Mobile number must be 10 digits';
        break;
      default:
        break;
    }
    
    return error;
  }, []);

  // Validate all fields
  const validateAllFields = useCallback(() => {
    const newFieldErrors = {};
    let isValid = true;

    const fieldsToValidate = ['purpose', 'name', 'email', 'password', 'mobile'];
    
    fieldsToValidate.forEach(field => {
      const error = validateField(field, formData[field]);
      // Only show error if field has been touched or has a value
      if (error && (touchedFields[field] || formData[field]?.trim())) {
        newFieldErrors[field] = error;
        isValid = false;
      }
    });

    setFieldErrors(newFieldErrors);
    return isValid;
  }, [formData, touchedFields, validateField]);

  // Check form validity without setting state
  const checkFormValidity = useCallback(() => {
    let isValid = true;
    const fieldsToValidate = ['purpose', 'name', 'email', 'password', 'mobile'];
    
    for (const field of fieldsToValidate) {
      const error = validateField(field, formData[field]);
      if (error) {
        isValid = false;
        break;
      }
    }
    
    return isValid;
  }, [formData, validateField]);

  // Update form validity whenever formData changes
  useEffect(() => {
    const isValid = checkFormValidity();
    setIsFormValid(isValid);
  }, [formData, checkFormValidity]);

  const handleBlur = useCallback((field) => {
    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    
    const error = validateField(field, formData[field]);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  }, [formData, validateField]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // For name field, only allow letters and spaces
    if (name === 'name') {
      const filteredValue = value.replace(/[^A-Za-z\s]/g, '');
      setFormData(prev => ({ ...prev, [name]: filteredValue }));
    } else {
      const limit = FIELD_LIMITS[name]?.max;
      if (limit && value.length > limit) {
        return;
      }
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear API error on any input change
    if (apiError) {
      setApiError('');
      setApiErrorType('error');
    }
  }, [fieldErrors, apiError]);

  const handlePurposeSelect = useCallback((purpose) => {
    setSelectedPurpose(purpose);
    setFormData(prev => ({ ...prev, purpose: purpose.value }));
    setShowPurposeDropdown(false);
    
    // Mark purpose as touched
    setTouchedFields(prev => ({ ...prev, purpose: true }));
    
    // Clear error for purpose
    if (fieldErrors.purpose) {
      setFieldErrors(prev => ({ ...prev, purpose: '' }));
    }
  }, [fieldErrors.purpose]);

  // API Integration function
  const registerUser = async () => {
    const requestBody = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      mobile: formData.mobile
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/auth/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      if (!response.ok) {
        let errorMessage = 'Registration failed';
        
        if (data && data.message) {
          errorMessage = data.message;
        } else if (data && data.error) {
          errorMessage = data.error;
        } else if (data && typeof data === 'string') {
          errorMessage = data;
        } else if (response.status === 409) {
          errorMessage = 'An account already exists with this email or mobile number. Please login instead.';
        } else if (response.status === 400) {
          errorMessage = 'Invalid information. Please check your details.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `Registration failed with status: ${response.status}`;
        }
        
        throw new Error(errorMessage);
      }
      
      if (!data.success || !data.userId || !data.otpTxnId) {
        let errorMessage = 'Invalid response from server';
        if (data && data.message) {
          errorMessage = data.message;
        }
        throw new Error(errorMessage);
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched when submitting
    const allFields = ['purpose', 'name', 'email', 'password', 'mobile'];
    const newTouchedFields = { ...touchedFields };
    allFields.forEach(field => {
      newTouchedFields[field] = true;
    });
    setTouchedFields(newTouchedFields);
    
    // Validate all fields
    const isValid = validateAllFields();
    
    if (!isValid) {
      return;
    }

    try {
      setIsLoading(true);
      setApiError('');
      setApiErrorType('error');
      
      const apiResponse = await registerUser();
      
      if (apiResponse.success === true && apiResponse.userId && apiResponse.otpTxnId) {
        setShowSuccessModal(true);
        
        setTimeout(() => {
          localStorage.setItem('tempUserData', JSON.stringify({
            ...formData,
            userId: apiResponse.userId,
            otpTxnId: apiResponse.otpTxnId
          }));
          
          navigate('/verify_mobileNumber', { 
            state: { 
              userId: apiResponse.userId,
              otpTxnId: apiResponse.otpTxnId,
              mobile: formData.mobile
            } 
          });
        }, 2000);
      }
    } catch (error) {
      const errorMessage = error.message || 'Registration failed. Please try again.';
      setApiError(errorMessage);
      
      // Check if it's a "user already exists" error
      if (errorMessage.toLowerCase().includes('already exists') || 
          errorMessage.toLowerCase().includes('already have an account')) {
        setApiErrorType('info');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate('/signin');
  };

  // Prevent unnecessary re-renders by memoizing expensive parts
  const selectedPurposeIcon = useMemo(() => {
    if (selectedPurpose) {
      return React.createElement(selectedPurpose.icon, { className: "w-4 h-4 text-white" });
    }
    return null;
  }, [selectedPurpose]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gsg-teal/5">
      {isLoading && <LoadingOverlay message="Creating your account..." />}
      
      <SuccessModal
        isOpen={showSuccessModal}
        title="Registration Successful!"
        message="Your account has been created. You'll be redirected to verification in a moment."
        showCountdown={true}
        countdownDuration={2}
      />
      
      <div className="relative min-h-screen flex">
        {/* Left Section */}
        <div className="hidden lg:flex lg:w-1/3 relative bg-gradient-to-br from-gsg-teal/5 via-white to-white">
          <div className="relative z-10 flex flex-col justify-center items-start w-full h-full p-12">
            <div className="max-w-lg">
              {/* Logo & Title */}
              <div className="mb-12 transition-all duration-1000">
                <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Start Your <span className="text-gsg-teal">Fundraiser</span>
                </h1>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Join thousands who have successfully raised funds for their causes.
                </p>
              </div>
              
              {/* Features */}
              <div className="transition-all duration-1000 delay-300">
                <div className="space-y-5">
                  {features.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <div 
                        key={index}
                        className={`p-5 bg-white/95 backdrop-blur-sm rounded-2xl border-2 transition-all duration-500 
                          ${currentFeature === index ? 'border-gsg-teal/30 shadow-xl shadow-gsg-teal/10 scale-[1.02]' : 
                            'border-gray-100 shadow-sm hover:border-gray-200'
                          }
                        `}
                      >
                        <div className="flex items-start space-x-4">
                          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                            <Icon className="w-7 h-7 text-white" />
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 text-lg mb-1.5">{feature.title}</h4>
                            <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Center Content */}
        <div className="w-full lg:w-2/3 flex flex-col justify-center items-center py-6 px-4 lg:px-8">
          <div className="w-full max-w-3xl mx-auto">
            {/* Centered Heading Section */}
            <div className="text-center mb-10 w-full">
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Create Your Fundraiser Account</h2>
              <p className="text-gray-600 mt-3 text-lg">Fill in all details to get started</p>
            </div>
            
            {/* Progress Indicator */}
            <div className="flex items-center justify-center space-x-12 mb-10 w-full">
              {[1, 2].map((item, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center text-base font-bold
                    ${index + 1 === 1 ? 'bg-gsg-teal text-white shadow-lg' : 
                      index + 1 < 1 ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500'}
                  `}>
                    {index + 1}
                  </div>
                  <div className="text-sm font-semibold text-gray-700 mt-3">
                    {index === 0 ? 'Registration' : 'Verification'}
                  </div>
                  <div className="h-1 w-full bg-gray-200 mt-3 rounded-full">
                    <div className={`h-1 w-full ${index + 1 < 1 ? 'bg-green-500' : index + 1 === 1 ? 'bg-gsg-teal' : 'bg-gray-200'} rounded-full`}></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Form Container */}
            <div className="w-full flex justify-center">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 w-full max-w-2xl">
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gsg-teal/5 to-white">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gsg-teal to-gsg-teal-dark flex items-center justify-center shadow-sm">
                      <UserCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Registration Details
                      </h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Step 1 of 2: Fill all details below
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Form Content */}
                <div className="px-6 py-5">
                  {apiError && (
                    <div className="mb-4">
                      <ErrorMessage 
                        message={apiError} 
                        type={apiErrorType} 
                        action={apiErrorType === 'info' ? {
                          label: 'Login Instead',
                          onClick: handleLoginRedirect
                        } : undefined}
                      />
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Purpose Selection */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2.5 text-left">
                        What are you raising funds for? <span className="text-red-500">*</span>
                      </label>
                      
                      <div className="relative">
                        <div 
                          ref={triggerRef}
                          className={`w-full px-4 py-3.5 bg-white border-2 rounded-lg cursor-pointer transition-all duration-200 hover:border-gsg-teal ${
                            fieldErrors.purpose && touchedFields.purpose ? 'border-red-500' : 'border-gray-200'
                          }`}
                          onClick={() => {
                            setShowPurposeDropdown(!showPurposeDropdown);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              {selectedPurpose ? (
                                <>
                                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${selectedPurpose.color} flex items-center justify-center`}>
                                    {selectedPurposeIcon}
                                  </div>
                                  <div>
                                    <span className="font-semibold text-gray-800 text-sm">{selectedPurpose.label}</span>
                                    <p className="text-xs text-gray-500 mt-0.5">{selectedPurpose.description}</p>
                                  </div>
                                </>
                              ) : (
                                <span className="text-gray-500 text-sm">Select a purpose</span>
                              )}
                            </div>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showPurposeDropdown ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                        
                        {/* Dropdown Container */}
                        {showPurposeDropdown && (
                          <div 
                            ref={dropdownRef}
                            className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden"
                            style={{ maxHeight: '280px' }}
                          >
                            <div className="sticky top-0 bg-white px-4 py-2.5 border-b border-gray-200">
                              <h4 className="font-semibold text-gray-700 text-left text-sm">Select Purpose</h4>
                              <p className="text-xs text-gray-500 mt-0.5 text-left">{purposeOptions.length} options</p>
                            </div>
                            <div className="overflow-y-auto" style={{ maxHeight: '230px' }}>
                              {purposeOptions.map((option) => (
                                <div
                                  key={option.value}
                                  className={`p-2.5 cursor-pointer transition-all hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                                    selectedPurpose?.value === option.value ? 'bg-gsg-teal/5' : ''
                                  }`}
                                  onClick={() => handlePurposeSelect(option)}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${option.color} flex items-center justify-center flex-shrink-0`}>
                                      {React.createElement(option.icon, { className: "w-4 h-4 text-white" })}
                                    </div>
                                    <div className="min-w-0">
                                      <div className="font-medium text-gray-800 text-sm truncate">{option.label}</div>
                                      <div className="text-xs text-gray-500 truncate">{option.description}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {fieldErrors.purpose && touchedFields.purpose && (
                        <p className="mt-1 text-sm text-red-600 text-left">
                          {fieldErrors.purpose}
                        </p>
                      )}
                    </div>

                    {/* Name and Email in Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Name Field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            onBlur={() => handleBlur('name')}
                            placeholder="Enter your name"
                            className={`
                              w-full pl-10 pr-3.5 py-3 rounded-lg border-2 transition-all duration-300
                              focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                              placeholder:text-gray-400 text-gray-900 text-sm
                              ${fieldErrors.name && touchedFields.name 
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                                : 'border-gray-200 hover:border-gray-300'
                              }
                            `}
                            maxLength={FIELD_LIMITS.name.max}
                          />
                        </div>
                        {fieldErrors.name && touchedFields.name && (
                          <p className="mt-1 text-xs text-red-600 text-left">
                            {fieldErrors.name}
                          </p>
                        )}
                      </div>

                      {/* Email Field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={() => handleBlur('email')}
                            placeholder="Enter your email"
                            className={`
                              w-full pl-10 pr-3.5 py-3 rounded-lg border-2 transition-all duration-300
                              focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                              placeholder:text-gray-400 text-gray-900 text-sm
                              ${fieldErrors.email && touchedFields.email 
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                                : 'border-gray-200 hover:border-gray-300'
                              }
                            `}
                            maxLength={FIELD_LIMITS.email.max}
                          />
                        </div>
                        {fieldErrors.email && touchedFields.email && (
                          <p className="mt-1 text-xs text-red-600 text-left">
                            {fieldErrors.email}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Password and Mobile in Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Password Field */}
{/* Password Field */}
{/* Password Field */}
<div>
  <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
    Password <span className="text-red-500">*</span>
  </label>
  <div className="relative">
    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
    
    <input
      type={showPassword ? "text" : "password"}
      name="password"
      value={formData.password}
      onChange={handleChange}
      onBlur={() => handleBlur('password')}
      placeholder="Create secure password"
      className={`
        w-full pl-10 pr-12 py-3 rounded-lg border-2 transition-all duration-300
        focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
        placeholder:text-gray-400 text-gray-900 text-sm
        ${fieldErrors.password && touchedFields.password 
          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
          : 'border-gray-200 hover:border-gray-300'
        }
      `}
      maxLength={FIELD_LIMITS.password.max}
    />
    
    {/* Eye button - Make sure this is inside the relative div */}
    <button
      type="button"
      onClick={() => setShowPassword(!showPassword)}
      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
    >
      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
  
  {fieldErrors.password && touchedFields.password && (
    <p className="mt-1 text-xs text-red-600 text-left">
      {fieldErrors.password}
    </p>
  )}
</div>

                      {/* Mobile Field */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2 text-left">
                          Mobile Number <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="tel"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            onBlur={() => handleBlur('mobile')}
                            placeholder="10-digit mobile number"
                            className={`
                              w-full pl-10 pr-3.5 py-3 rounded-lg border-2 transition-all duration-300
                              focus:outline-none focus:ring-1 focus:ring-gsg-teal/30 focus:border-gsg-teal
                              placeholder:text-gray-400 text-gray-900 text-sm
                              ${fieldErrors.mobile && touchedFields.mobile 
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' 
                                : 'border-gray-200 hover:border-gray-300'
                              }
                            `}
                            maxLength={FIELD_LIMITS.mobile.max}
                          />
                        </div>
                        {fieldErrors.mobile && touchedFields.mobile && (
                          <p className="mt-1 text-xs text-red-600 text-left">
                            {fieldErrors.mobile}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Password hint - Simple message only */}
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-blue-700 text-left">
                          Password must be at least {FIELD_LIMITS.password.min} characters long.
                        </p>
                      </div>
                    </div>
                    
                    {/* Mobile Info */}
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-blue-700 text-left">
                          We'll send a verification code to your mobile number to secure your account.
                        </p>
                      </div>
                    </div>
                    
                    {/* Submit Button */}
                    <div className="mt-6 pt-5 border-t border-gray-100">
                      <button
                        type="submit"
                        disabled={!isFormValid || isLoading}
                        className={`
                          w-full px-6 py-2.5 rounded-lg transition-all duration-300 font-semibold text-sm
                          ${!isFormValid || isLoading
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-gsg-teal to-gsg-teal-dark text-white hover:shadow-md hover:shadow-gsg-teal/25'
                          }
                          flex items-center justify-center
                        `}
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Creating Account...
                          </>
                        ) : (
                          <>
                            Register & Verify Mobile
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>

                  {/* Login Link */}
                  <div className="text-center pt-4 mt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-600">
                      Already have an account?{' '}
                      <a href="/signin" className="font-semibold text-gsg-teal hover:text-gsg-teal-dark hover:underline transition-colors">
                        Login here
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center mt-8 w-full">
              <p className="text-xs text-gray-500">
                By continuing, you agree to our Terms & Privacy Policy
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundraiserForm;